import React, { useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";
import VisibilityIcon from "@material-ui/icons/Visibility";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { TravelCycleOutcomes } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCycleHardwareKind, TravelCycleHardwareRow } from "@shared/interfaces/travel-cycle-analysis.interface";
import NewDataTable, { DataTableActions, DataTableColumns } from "@molecules/NewDataTable";
import ButtonTable from "@components/Button/ButtonTable";
import { sortByNatural, sortByText, sortByValue } from "@utils/data-table-sorting";
import utils from "@utils/useful-functions";

import { HARDWARE_KINDS } from "../../constants";
import { formatCount, formatPercent, percentOf } from "../../cycleUtils";
import { ChartContainer, CycleTable, HardwareTabs } from "../../styles";

/** Outcome of the travels each kind of hardware made fail. */
const KIND_OUTCOME: Record<TravelCycleHardwareKind, TravelCycleOutcomes> = {
  sensor_stopped: TravelCycleOutcomes.SENSOR_STOPPED,
  sensor_intermittent: TravelCycleOutcomes.SENSOR_INTERMITTENT,
  tracker_without_signal: TravelCycleOutcomes.TRACKER_WITHOUT_SIGNAL
};

/** Props for the HardwareToCheck organism. */
interface HardwareToCheckProps {
  rows: TravelCycleHardwareRow[];
  /** Open the failed travels of a truck for the given outcome. */
  onOpenVehicle: (idVehicle: string, outcome: TravelCycleOutcomes) => void;
}

/** Date as dd/mm/yyyy, or a dash. */
const formatDate = (date: Date | string | null): string => (date ? String(utils.formatDateIfHave(date, "date")) : "—");

/**
 * Discharge sensors (stopped or intermittent) and trackers that made travels fail in the filter,
 * with the discharges the sensor registered and the last data each hardware sent.
 */
const HardwareToCheck: React.FC<HardwareToCheckProps> = ({ rows, onOpenVehicle }) => {

  const { t } = useTranslation();
  const [kind, setKind] = useState<TravelCycleHardwareKind | "all">("all");
  const shown = useMemo(() => rows.filter((row) => kind === "all" || row.kind === kind), [rows, kind]);
  const countOf = (k: TravelCycleHardwareKind | "all") => rows.filter((row) => k === "all" || row.kind === k).length;
  const isSensor = (row: TravelCycleHardwareRow) => row.kind !== "tracker_without_signal";

  const columns: DataTableColumns[] = [
    {
      title: t(TravelAnalysisMessages.columnVehicle),
      data: sortByNatural<TravelCycleHardwareRow>((row) => row.code, (row) => ReactDOMServer.renderToString(
        <span><b>{row.code}</b><span className="cell-caption">{row.plate ?? ""}</span></span>
      )),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnPlant),
      data: sortByText<TravelCycleHardwareRow>((row) => row.plantName ?? "", (row) => ReactDOMServer.renderToString(
        <span>{row.plantName ?? "—"}<span className="cell-caption">{row.regionalName ?? ""}</span></span>
      )),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnProblem),
      data: sortByText<TravelCycleHardwareRow>((row) => t(HARDWARE_KINDS[row.kind].labelKey), (row) => ReactDOMServer.renderToString(
        <span className="kind-pill" style={{ background: HARDWARE_KINDS[row.kind].background, color: HARDWARE_KINDS[row.kind].color }}>
          {t(HARDWARE_KINDS[row.kind].labelKey)}
        </span>
      )),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnDischarges),
      data: sortByValue<TravelCycleHardwareRow>(
        (row) => (isSensor(row) ? percentOf(row.dischargeDetected, row.reachedJobsite) : null),
        (row) => (isSensor(row) ? ReactDOMServer.renderToString(
          <span className="rate-cell">
            <span>
              {`${formatCount(row.dischargeDetected)} ${t(TravelAnalysisMessages.wordOf)} ${formatCount(row.reachedJobsite)} · `
                + formatPercent(percentOf(row.dischargeDetected, row.reachedJobsite), 0)}
            </span>
            <span className="rate-bar">
              <i style={{ width: `${percentOf(row.dischargeDetected, row.reachedJobsite)}%`, background: HARDWARE_KINDS[row.kind].color }} />
            </span>
          </span>
        ) : "—")
      ),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnFailedTravels),
      data: sortByValue<TravelCycleHardwareRow>((row) => row.affected, (row) => ReactDOMServer.renderToString(
        <span>
          <b>{formatCount(row.affected)}</b>
          <span className="cell-caption">{`${t(TravelAnalysisMessages.wordOf)} ${formatCount(row.travels)}`}</span>
        </span>
      )),
      className: "dt-center",
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnLastUnload),
      data: sortByValue<TravelCycleHardwareRow>(
        (row) => (isSensor(row) && row.lastUnloadDate ? new Date(row.lastUnloadDate).getTime() : null),
        (row) => (isSensor(row) ? formatDate(row.lastUnloadDate) : "—")
      ),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnLastSignal),
      data: sortByValue<TravelCycleHardwareRow>(
        (row) => (row.lastSignalDate ? new Date(row.lastSignalDate).getTime() : null),
        (row) => ReactDOMServer.renderToString(
          <span>
            {formatDate(row.lastSignalDate)}
            <span className="cell-caption">{isSensor(row) ? t(TravelAnalysisMessages.hwSensorShort) : t(TravelAnalysisMessages.hwTrackerShort)}</span>
          </span>
        )
      ),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnActions),
      orderable: false,
      searchable: false,
      data: () => ReactDOMServer.renderToString(
        <ButtonTable className="btn-view-travels" aria-disabled>
          <VisibilityIcon fontSize="small" />
        </ButtonTable>
      ),
      filterable: false
    }
  ];

  const actions: DataTableActions<TravelCycleHardwareRow>[] = [
    { ref: ".btn-view-travels", callback: (row: TravelCycleHardwareRow) => onOpenVehicle(row.idVehicle, KIND_OUTCOME[row.kind]) }
  ];

  return (
    <ChartContainer>
      <HardwareTabs>
        <div>
          <div className="chart-title">{t(TravelAnalysisMessages.hardwareTitle)}</div>
          <p className="chart-hint">{t(TravelAnalysisMessages.hardwareHint)}</p>
        </div>
        <div className="hw-tabs" role="group">
          {(["all", "sensor_stopped", "sensor_intermittent", "tracker_without_signal"] as const).map((k) => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {`${k === "all" ? t(TravelAnalysisMessages.hwAll) : t(HARDWARE_KINDS[k].labelKey)} (${countOf(k)})`}
            </button>
          ))}
        </div>
      </HardwareTabs>
      {!shown.length ? (
        <div className="chart-empty">{t(TravelAnalysisMessages.noHardware)}</div>
      ) : (
        <CycleTable>
          <NewDataTable
            key={kind}
            data={shown}
            columns={columns}
            actions={actions}
            settings={{ order: [[4, "desc"]], pageLength: 10 }}
            showSearching
            scrollHeight=""
          />
        </CycleTable>
      )}
    </ChartContainer>
  );
};

export default HardwareToCheck;
